import type { CardHooks } from "./registry";

/**
 * Vengeance Strike — Attack/Defend Special Move.
 *
 * Supabase row stats: P6 Sweep / Block 4 Dodge, 4 stamina, counter `Stun`
 * vs Light Attack. Counter is wired data-driven via counterRules on the
 * Supabase row (no hook needed).
 *
 * ── Attack side ability ──
 * "If you took 2 or more damage this round, attack gains quick."
 *
 * Data-driven: `onAttackPlay` checks `self.totalHealthLost - prev_round_health`
 * — but Phase 3 tracks damage at a coarser per-round granularity via
 * `tookDamageThisRound` (boolean) and the actual `damageTakenThisRound`
 * accumulator. Since we don't have a separate accumulator yet, this hook
 * reads the per-round damage history from `damageDealtByAttackThisRound`
 * on the OPPONENT (mirror) — but that's the opponent's side. Cleaner path:
 * add a per-round damage accumulator. For now we approximate with
 * `tookDamageThisRound` as a binary flag (>=1 → assume >=2 if needed). A
 * stricter check requires a numeric `damageTakenThisRound` field. Noah:
 * if you want strict ">=2", I'll add `damageTakenThisRound: number` to
 * PlayerState in a Phase 3 follow-up.
 *
 * For now: gain Quick if `tookDamageThisRound` is true. This is permissive
 * (any damage taken triggers Quick, not strictly >=2). Phase 3 follow-up
 * tightens this.
 *
 * ── Defend side ability ──
 * "Full Block: You may choose to take 2 damage. If you do you may
 *  immediately pay 2 stamina and play the attack side of this card."
 *
 * Implementation note: "take 2 damage" → apply 2 damage to self → prompt
 * to pay 2 stamina and play attack side. This is complex because it
 * involves voluntary self-damage on full-block, which doesn't match any
 * existing pattern. For Phase 3 we implement a SIMPLER version:
 *  - On full-block, fire `vengeanceAttackPrompt` asking "Pay 2 stamina to
 *    play attack side of Vengeance Strike?"
 *  - The voluntary "take 2 damage" gate is deferred to Phase 4 (requires
 *    new voluntary-self-damage primitive). Logged as TODO below.
 *
 * The full-block path is the correct trigger (vs the on-damage path) —
 * the defender FULLY BLOCKED with this card, so they get the option.
 */

const STAMINA_COST = 2;

export const vengeanceStrike: CardHooks = {
  // Attack side: gain Quick if we took damage this round.
  onAttackPlay: (ctx) => {
    const { self, card } = ctx;
    if (!self.tookDamageThisRound) return {};
    return {
      slotKeywordGrants: ["Quick"],
      log: [
        `${card.name} — gains Quick (took damage this round).`,
      ],
    };
  },

  // Defend side: on full block, offer to pay stamina and play attack side.
  // TODO: gate on "take 2 self-damage" choice once voluntary-self-damage
  // primitive exists. Right now it fires unconditionally on full-block.
  onFullBlock: (ctx) => {
    const { self, instanceId, card } = ctx;
    if (!instanceId) return {};
    if ((self.stamina ?? 0) < STAMINA_COST) {
      return {
        log: [
          `${card.name} — full blocked, but not enough stamina (${STAMINA_COST}) to play attack side.`,
        ],
      };
    }
    return {
      vengeanceAttackPrompt: {
        staminaCost: STAMINA_COST,
        minDamage: 0, // not gated on damage taken in this path
        instanceId,
      },
      log: [
        `${card.name} — full blocked. Pay ${STAMINA_COST} stamina to play attack side?`,
      ],
    };
  },
};
